import React, {useEffect, useMemo, useState} from "react";
import{ useSelector} from "react-redux";
import CustomLoaderSmall from "../../../src/components/Layout/CustomLoaderSmall";

import {
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    Clock3,
    Download,
    Timer,
    CheckCircle2,
    MapPin,
    Smartphone,
    Users2,
    MessageCircleMore,
} from "lucide-react";


import NavigationSideBar from "../../components/Layout/Graph & Tables/NavigationSideBar";
import DashboardHeader from "../../components/Layout/DashBoardHeader";
import StackCard from "../../components/Layout/StackCard";

import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import {useNavigate, useParams} from "react-router-dom";
import {useListProjectHoursPerWeekQuery,
        useGetCurrentWorkSessionQuery} from "../../../src/features/attendanceApiSlice";

import AttendanceChatWindow from "../../../src/screens/mainscreen/AttendanceChatWindow";

export default function WeeklyTimeSheetScreen() {

    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    const [weekOffset, setWeekOffset] = useState<number>(0);

    const {userInfo} = useSelector((state: any) => state).auth;

    const userId = userInfo?.id;

    const navigate = useNavigate();

    const {id} = useParams();

    const {data: currentSession } = useGetCurrentWorkSessionQuery(userId);

    const {data: workHours, isLoading} = useListProjectHoursPerWeekQuery(id);

    const [isAttendanceChatOpen, setIsAttendanceChatOpen] = useState(false);
    // --------------------------------------------------------
    // Calculate summary
    // --------------------------------------------------------

    const STANDARD_WEEKLY_HOURS = 40;

    // Find Work Week
    const getWeekRange = (date: Date) => {
        const current = new Date(date);

        const day = current.getDay();

        // Monday = first day of week
        const mondayOffset = day === 0 ? -6 : 1 - day;

        const weekStart = new Date(current);

        weekStart.setDate(
            current.getDate() + mondayOffset
        );

        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);

        weekEnd.setDate(
            weekStart.getDate() + 5
        );

        weekEnd.setHours(23, 59, 59, 999);

        return {
            weekStart,
            weekEnd,
        };
    };

    const {
        weekStart,
        weekEnd,
    } = useMemo(() => {
        const date = new Date();

        date.setDate(
            date.getDate() + weekOffset * 7
        );

        return getWeekRange(date);
    }, [weekOffset]);

    const myWeeklyHours = useMemo(() => {
        if (!Array.isArray(workHours)) {
            return [];
        }

        return workHours.filter((item: any) => {
            if (!item.signInTime) {
                return false;
            }

            const signInDate = new Date(item.signInTime);

            return (
                signInDate >= weekStart &&
                signInDate <= weekEnd
            );
        });
    }, [
        workHours,
        weekStart,
        weekEnd,
    ]);

    // Figure out working days
    const workingDays = useMemo(() => {
        const uniqueDays = new Set(
            myWeeklyHours
                .filter(
                    (item: any) =>
                        item.status === "Completed" &&
                        Number(item.totalHours ?? 0) > 0
                )
                .map((item: any) =>
                    new Date(item.signInTime)
                        .toISOString()
                        .split("T")[0]
                )
        );

        return uniqueDays.size;
    }, [myWeeklyHours]);

    const weeklyTableDays = useMemo(() => {
        return Array.from({ length: 6 }, (_, index) => {
            const date = new Date(weekStart);

            date.setDate(weekStart.getDate() + index);

            return {
                date,
                dateKey: date.toISOString().split("T")[0],
                dayName: date.toLocaleDateString("en-US", {
                    weekday: "long",
                }),
            };
        });
    }, [weekStart]);

    const weeklyEmployeeRows = useMemo(() => {
        if (!Array.isArray(myWeeklyHours)) {
            return [];
        }

        const employeeMap = new Map<string, any>();

        myWeeklyHours.forEach((session: any) => {
            if (!session.userId || !session.signInTime) {
                return;
            }

            const userId = session.userId;

            if (!employeeMap.has(userId)) {
                employeeMap.set(userId, {
                    userId,
                    userName: session.userName ?? "Unknown Employee",
                    days: {},
                    totalHours: 0,
                    exceedsDailyLimit: false,
                });
            }

            const employee = employeeMap.get(userId);

            const dateKey = new Date(session.signInTime)
                .toISOString()
                .split("T")[0];

            const hours = Number(session.totalHours ?? 0);


            employee.days[dateKey] =
                (employee.days[dateKey] ?? 0) + hours;

            employee.totalHours += hours;

            employee.exceedsDailyLimit = Object.values(employee.days).some(
                (dailyHours: any) => Number(dailyHours) > 12
            )

        });

        return Array.from(employeeMap.values());
    }, [myWeeklyHours]);


    const totalWeeklyHours = useMemo(() => {
        return myWeeklyHours.reduce(
            (total: number, item: any) =>
                total + Number(item.totalHours ?? 0),
            0
        );
    }, [myWeeklyHours]);

    const formatDisplayDate = (dateString: string) => {
        const [year, month, day] = dateString.split("-").map(Number);

        return new Date(year, month - 1, day).toLocaleDateString(
            "en-US",
            {
                month: "short",
                day: "numeric",
            }
        );
    };

    const regularHours = Math.min(
        totalWeeklyHours,
        STANDARD_WEEKLY_HOURS
    );

    const overtimeHours = Math.max(
        totalWeeklyHours - STANDARD_WEEKLY_HOURS,
        0
    );

    const formatHours = (decimalHours: number) => {
        const totalMinutes = Math.round(
            decimalHours * 60
        );

        const hours = Math.floor(
            totalMinutes / 60
        );

        const minutes = totalMinutes % 60;

        return `${hours}h ${minutes}m`;
    };

    const formatDate = (date: Date) => {
        return date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
        });
    };

    const regularHoursDisplay =
        formatHours(regularHours);

    const overTimeHoursDisplay =
        formatHours(overtimeHours);

    const totalHoursDisplay =
        formatHours(totalWeeklyHours);
    // --------------------------------------------------------
    // Week navigation
    // --------------------------------------------------------

    const handlePreviousWeek = () => {
        setWeekOffset((prev) => prev - 1);
    };


    const handleNextWeek = () => {
        setWeekOffset((prev) => prev + 1);
    };


    const handleCurrentWeek = () => {
        setWeekOffset(0);
    };

    // --------------------------------------------------------
    // Export
    // --------------------------------------------------------

    const handleExport = () => {
        console.log("Export weekly timesheet");
    };

    // --------------------------------------------------------
    // Selected day
    // --------------------------------------------------------

    const selectedDay = weeklyEmployeeRows.find(
        (day) => day.date === selectedDate
    );

    const getEmployeeDayHours = (
        employee: any,
        dateKey: string,
    ) => {
      return Number(employee.days?.[dateKey] ?? 0);
    };

    {/* Pagination */}
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(6);

    const totalItems = weeklyEmployeeRows?.length;

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const currentAssignedEmployees = weeklyEmployeeRows?.slice(
        indexOfFirstItem,
        indexOfLastItem
    );

// Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, []);

// Keep current page valid when records change
    useEffect(() => {
        setCurrentPage((page) =>
            Math.min(page, Math.max(totalPages, 1))
        );
    }, [totalPages]);


    return (
        <main>
            <Helmet>
                <title>Weekly Timesheet</title>

                <meta
                    name="description"
                    content="Employee weekly timesheet"
                />
            </Helmet>

            {isLoading ? (
                <CustomLoaderSmall />
            ) : (
                <motion.div
                    initial={{
                        opacity: 0,
                        y: 20,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                    }}
                    transition={{
                        delay: 0.2,
                    }}
                >

                    <div className="
                    bg-[#0A0A0A]
                    text-white
                    flex
                    min-h-dvh

                ">

                        <div className={"hidden lg:block"}>

                            <NavigationSideBar />
                        </div>

                        <div className="flex-1
                                        flex
                                        min-w-0
                                        flex-col">

                            <DashboardHeader projectId={id} />


                            <main className="
                            w-full
                            max-w-[1600px]
                            mx-auto

                            px-4
                            sm:px-6
                            md:px-8
                            lg:px-10

                            py-5
                            sm:py-6
                            md:py-8

                            pb-[env(safe-area-inset-bottom)]
                        ">


                                {/* ==================================================
                                Employee Header
                            ================================================== */}

                                <section className="
                                flex
                                flex-col
                                lg:flex-row
                                lg:items-center
                                lg:justify-between
                                gap-4
                                mb-6
                            ">

                                    <div>
                                        <div className="
                                        flex
                                        items-center
                                        gap-3
                                    ">

                                            <div className="
                                           flex
                                           h-11
                                           w-11
                                           shrink-0
                                           items-center
                                           justify-center
                                           rounded-full
                                         bg-blue-500/10
                                           border
                                         border-blue-500/20
                                        ">
                                                <Users2
                                                    size={24}
                                                    className="text-blue-600"
                                                />
                                            </div>


                                            <div className={"min-w-0"}>

                                                <h1 className="
                                                text-xl
                                                sm:text-2xl
                                                font-bold
                                                text-white
                                            ">
                                                    Weekly Timesheet
                                                </h1>

                                            </div>

                                        </div>

                                    </div>

                                    <div className={"justify-center flex flex-row gap-4"}>
                                        <button
                                            type="button"
                                            onClick={() => setIsAttendanceChatOpen(true)}
                                            aria-label="Open attendance assistant"
                                            title="Ask the attendance assistant"
                                            className="
                                                        flex items-center justify-center gap-2
                                                        rounded-xl border border-gray-700
                                                        px-3 py-2 text-blue-400
                                                        transition hover:bg-gray-800 hover:text-blue-300"
                                        >
                                            <MessageCircleMore size={28} />
                                            <span className="hidden sm:inline text-sm">
                                                Chat
                                            </span>
                                        </button>

                                        <button
                                            onClick={handleExport}
                                            className="
                                        flex
                                        items-center
                                        justify-center
                                        gap-2
                                        px-4
                                        py-2
                                        bg-blue-600
                                        hover:bg-blue-500
                                        rounded-lg
                                        text-sm
                                        font-medium
                                        transition-colors
                                    "
                                        >
                                            <Download size={17} />

                                            Export Timesheet
                                        </button>
                                    </div>


                                </section>


                                {/* ==================================================
                                Summary Cards
                            ================================================== */}

                                <div className="
                                grid
                                grid-cols-1
                                sm:grid-cols-2
                                md:grid-cols-3
                                lg:grid-cols-4
                                gap-4
                                mb-6
                            ">

                                    <StackCard
                                        icon={Clock3}
                                        name="Regular Hours"
                                        value={regularHoursDisplay}
                                        color="#6366F1"
                                    />


                                    <StackCard
                                        icon={Timer}
                                        name="Overtime"
                                        value={overTimeHoursDisplay}
                                        color="#8B5CF6"
                                    />


                                    <StackCard
                                        icon={CalendarDays}
                                        name="Total Hours"
                                        value={totalHoursDisplay}
                                        color="#06B6D4"
                                    />


                                    <StackCard
                                        icon={CheckCircle2}
                                        name="Status"
                                        value="Submitted"
                                        color="#22C55E"
                                    />

                                </div>


                                {/* ==================================================
                                Week Navigation
                            ================================================== */}

                                <div className="
                                bg-[#101010]
                                border
                                border-gray-800
                                rounded-lg
                                p-4
                                mb-6
                            ">

                                    <div className="
                                    flex
                                    flex-col
                                    md:flex-row
                                    items-center
                                    justify-between
                                    gap-4
                                ">


                                        <button
                                            onClick={handlePreviousWeek}
                                            className="
                                            flex
                                            items-center
                                            gap-2
                                            px-4
                                            py-2
                                            bg-gray-800
                                            hover:bg-gray-700
                                            rounded-lg
                                            text-sm
                                            transition-colors
                                        "
                                        >
                                            <ChevronLeft size={17} />

                                            Previous Week
                                        </button>


                                        <div className="text-center">

                                            <p className="
                                            text-white
                                            font-semibold
                                        ">
                                                {formatDate(weekStart)} -{formatDate(weekEnd)},{""}
                                                {weekEnd.getFullYear()}
                                            </p>

                                            <p className="
                                            text-gray-500
                                            text-xs
                                            mt-1
                                        ">
                                                Weekly Ending
                                            </p>

                                            {weekOffset !== 0 && (
                                                <button
                                                    onClick={handleCurrentWeek}
                                                    className="
                                                    text-blue-400
                                                    hover:text-blue-300
                                                    text-xs
                                                    mt-2
                                                "
                                                >
                                                    Return to current week
                                                </button>
                                            )}
                                        </div>


                                        <button
                                            onClick={handleNextWeek}
                                            className="
                                            flex
                                            items-center
                                            gap-2
                                            px-4
                                            py-2
                                            bg-gray-800
                                            hover:bg-gray-700
                                            rounded-lg
                                            text-sm
                                            transition-colors
                                        "
                                        >
                                            Next Week

                                            <ChevronRight size={17} />
                                        </button>

                                    </div>

                                </div>


                                {/* ==================================================
                                Weekly Attendance Table
                            ================================================== */}

                                <div className="
                                bg-[#101010]
                                border
                                border-gray-800
                                rounded-lg
                                overflow-hidden
                                mb-6
                            ">
                                    <div className="
                                    p-4
                                    border-b
                                    border-gray-800
                                ">
                                        <div className="
                                        flex
                                        flex-col
                                        sm:flex-row
                                        sm:items-center
                                        sm:justify-between
                                        gap-2
                                    ">
                                            <div>
                                                <h2 className="
                                                text-lg
                                                font-semibold
                                                text-white
                                            ">
                                                    Weekly Attendance
                                                </h2>

                                                <p className="
                                                text-sm
                                                text-gray-500
                                                mt-1
                                            ">
                                                    Daily Employee Work Sessions
                                                </p>
                                            </div>

                                            <span className="
                                            text-xs
                                            px-3
                                            py-1
                                            rounded-full
                                            bg-blue-500/10
                                            text-blue-400
                                            border
                                            border-blue-500/20
                                        ">
                                            {workingDays} Working Days
                                        </span>

                                        </div>

                                    </div>


                                    <div className="overflow-x-auto">

                                        <table className="w-full text-left">

                                            <thead>
                                            <tr>
                                                <th className="p-4 text-xs font-semibold text-gray-400">
                                                    Employee
                                                </th>

                                                {weeklyTableDays.map((day) => (
                                                    <th
                                                        key={day.dateKey}
                                                        className="p-4 text-xs font-semibold text-gray-400 text-center"
                                                    >
                                                        {day.date.toLocaleDateString("en-US", {
                                                            weekday: "short",
                                                        })}
                                                    </th>
                                                ))}

                                                <th className="p-4 text-xs font-semibold text-gray-400">
                                                    Total
                                                </th>

                                                <th className="p-4 text-xs font-semibold text-gray-400">
                                                    Status
                                                </th>
                                            </tr>
                                            </thead>

                                            <tbody>
                                            {currentAssignedEmployees.map((employee) => (
                                                <tr
                                                    key={employee.userId}
                                                    className="border-t border-gray-800 hover:bg-gray-900/60"
                                                >

                                                    {/* Employee */}
                                                    <td className="p-4">
                                                        <div className="flex items-center gap-3">

                                                            <div className="
                        h-10
                        w-10
                        rounded-full
                        bg-gradient-to-r
                        from-purple-600
                        to-blue-500
                        flex
                        items-center
                        justify-center
                        text-white
                        font-semibold
                    ">
                                                                {employee.userName
                                                                    ?.split(" ")
                                                                    .map((name: string) => name[0])
                                                                    .slice(0, 2)
                                                                    .join("")
                                                                    .toUpperCase()}
                                                            </div>

                                                            <span className="text-gray-200 font-medium">
                        {employee.userName}
                    </span>

                                                        </div>
                                                    </td>

                                                    {/* Monday - Saturday */}
                                                    {weeklyTableDays.map((day) => {
                                                        const hours = getEmployeeDayHours(
                                                            employee,
                                                            day.dateKey
                                                        );

                                                        return (
                                                            <td
                                                                key={day.dateKey}
                                                                className={`p-4 text-center ${hours > 12 ? "text-red-500": "text-white"}`}
                                                            >
                                                                <div className={"flex flex-col items-center gap-1"}>
                                                                    <span className={"font-semibold"}>
                                                                        {hours > 0
                                                                            ? hours.toFixed(2)
                                                                            : "—"}
                                                                    </span>

                                                                    {hours > 12 && (
                                                                        <span className={"inline-flex items-center gap-1 rounded-full" +
                                                                            "border border-red-500 bg-red-500/10 px-2 py-1 text-sm text-red-600"}>
                                                                            <Clock3 size={16} />
                                                                            Exceeds Limit
                                                                        </span>
                                                                    )}

                                                                </div>

                                                            </td>
                                                        );
                                                    })}

                                                    {/* Total */}
                                                    <td className="
                                                                    p-4
                                                                    text-center
                                                                    text-white
                                                                    font-semibold
                                                                ">
                                                        <span className={`text-sm font-semibold ${employee.exceedsDailyLimit ? "text-red-500" : "text-white"}`}>
                                                            {employee.totalHours.toFixed(2)}
                                                        </span>

                                                    </td>

                                                    {/* Status */}
                                                    <td className="p-4">
                                                                    <span
                                                                        className="
                                                                            px-2
                                                                            py-1
                                                                            text-xs
                                                                            rounded-full
                                                                            bg-green-500/10
                                                                            text-green-400
                                                                            border
                                                                            border-green-500/20
                                                                        "
                                                                    >
                                                                        {employee.totalHours <= 20
                                                                            ? "Working"
                                                                            : "No Working"}
                                                                    </span>
                                                    </td>

                                                </tr>
                                            ))}
                                            </tbody>

                                        </table>

                                        {/* Desktop Pagination Button */}
                                        {/* Pagination */}
                                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-800 p-4">

                                            {/* Records information */}
                                            <div className="text-sm text-gray-400">
                                                Showing{" "}
                                                {totalItems === 0 ? 0 : indexOfFirstItem + 1}
                                                {" - "}
                                                {Math.min(indexOfLastItem, totalItems)}
                                                {" of "}
                                                {totalItems} employees
                                            </div>

                                            {/* Items per page */}
                                            <div className="flex items-center gap-2">
                                                <label
                                                    htmlFor="itemsPerPage"
                                                    className="text-sm text-gray-400"
                                                >
                                                    Rows per page:
                                                </label>

                                                <select
                                                    id="itemsPerPage"
                                                    value={itemsPerPage}
                                                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                                    className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
                                                >
                                                    <option value={5}>5</option>
                                                    <option value={6}>6</option>
                                                    <option value={10}>10</option>
                                                    <option value={20}>20</option>
                                                    <option value={50}>50</option>
                                                </select>
                                            </div>

                                            {/* Page navigation */}
                                            <div className="flex items-center gap-2">

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                                                    }
                                                    disabled={currentPage === 1 || totalItems === 0}
                                                    className="flex items-center gap-1 rounded-lg bg-gray-800 px-3 py-2 text-sm text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                                                >
                                                    <ChevronLeft size={16} />
                                                    Prev
                                                </button>

                                                <span className="min-w-[90px] text-center text-sm text-gray-300">
            Page {totalItems === 0 ? 0 : currentPage} of {totalPages}
        </span>

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setCurrentPage((prev) =>
                                                            Math.min(prev + 1, totalPages)
                                                        )
                                                    }
                                                    disabled={currentPage >= totalPages || totalItems === 0}
                                                    className="flex items-center gap-1 rounded-lg bg-gray-800 px-3 py-2 text-sm text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                                                >
                                                    Next
                                                    <ChevronRight size={16} />
                                                </button>

                                            </div>
                                        </div>

                                    </div>

                                </div>


                                {/* ==================================================
                                Selected Day Detail
                            ================================================== */}

                                {selectedDay && (

                                    <motion.div
                                        initial={{
                                            opacity: 0,
                                            height: 0,
                                        }}
                                        animate={{
                                            opacity: 1,
                                            height: "auto",
                                        }}
                                        className="
                                        bg-[#101010]
                                        border
                                        border-gray-800
                                        rounded-lg
                                        overflow-hidden
                                        mb-6
                                    "
                                    >

                                        <div className="
                                        p-4
                                        border-b
                                        border-gray-800
                                        flex
                                        items-center
                                        justify-between
                                    ">

                                            <div>

                                                <h2 className="
                                                text-lg
                                                font-semibold
                                                text-white
                                            ">
                                                    {selectedDay.day}
                                                </h2>

                                                <p className="
                                                text-sm
                                                text-gray-500
                                            ">
                                                    Detailed Work Session
                                                </p>

                                            </div>


                                            <button
                                                onClick={() =>
                                                    setSelectedDate(null)
                                                }
                                                className="
                                                text-gray-500
                                                hover:text-white
                                                text-sm
                                            "
                                            >
                                                Close
                                            </button>

                                        </div>


                                        <div className="
                                        grid
                                        grid-cols-1
                                        sm:grid-cols-2
                                        lg:grid-cols-4
                                        gap-4
                                        p-4
                                    ">

                                            <div>
                                                <p className="
                                                text-xs
                                                text-gray-500
                                                uppercase
                                            ">
                                                    Project
                                                </p>

                                                <p className="
                                                text-gray-200
                                                mt-1
                                            ">
                                                    {selectedDay.projectName}
                                                </p>
                                            </div>


                                            <div>
                                                <p className="
                                                text-xs
                                                text-gray-500
                                                uppercase
                                            ">
                                                    Sign In
                                                </p>

                                                <p className="
                                                text-gray-200
                                                mt-1
                                            ">
                                                    {selectedDay.signInTime}
                                                </p>
                                            </div>


                                            <div>
                                                <p className="
                                                text-xs
                                                text-gray-500
                                                uppercase
                                            ">
                                                    Sign Out
                                                </p>

                                                <p className="
                                                text-gray-200
                                                mt-1
                                            ">
                                                    {selectedDay.signOutTime}
                                                </p>
                                            </div>


                                            <div>
                                                <p className="
                                                text-xs
                                                text-gray-500
                                                uppercase
                                            ">
                                                    Total
                                                </p>

                                                <p className="
                                                text-white
                                                font-semibold
                                                mt-1
                                            ">
                                                    {selectedDay.totalHours}
                                                </p>
                                            </div>


                                            <div>

                                                <div className="
                                                flex
                                                items-center
                                                gap-2
                                            ">
                                                    <MapPin
                                                        size={15}
                                                        className="
                                                        text-gray-500
                                                    "
                                                    />

                                                    <p className="
                                                    text-xs
                                                    text-gray-500
                                                    uppercase
                                                ">
                                                        Site
                                                    </p>
                                                </div>

                                                <p className="
                                                text-gray-200
                                                mt-1
                                            ">
                                                    {selectedDay.siteCode}
                                                </p>

                                            </div>


                                            <div>

                                                <div className="
                                                flex
                                                items-center
                                                gap-2
                                            ">
                                                    <Smartphone
                                                        size={15}
                                                        className="
                                                        text-gray-500
                                                    "
                                                    />

                                                    <p className="
                                                    text-xs
                                                    text-gray-500
                                                    uppercase
                                                ">
                                                        Device
                                                    </p>
                                                </div>

                                                <p className="
                                                text-gray-200
                                                mt-1
                                            ">
                                                    {selectedDay.deviceId}
                                                </p>

                                            </div>

                                        </div>

                                    </motion.div>

                                )}

                            </main>

                        </div>

                        <AttendanceChatWindow open={isAttendanceChatOpen} onClose={() => setIsAttendanceChatOpen(false)} />
                    </div>

                </motion.div>
            )}
        </main>
    );
}